<template>
    <div class="m-team mb-48">
    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-pulse text-lg"><span class="loader"></span> Laden...</div>
    </div>

    <template v-else>
      <form @submit.prevent class="m-team__form mt-8 mb-8 xl:mb-16">
        <input
          class="text-3xl md:text-4xl xl:text-8xl py-4 md:py-8 outline-none w-full"
          autofocus
          @input="onSearchInput"
          :placeholder="placeholder"
        />
      </form>

      <div v-if="filteredAbteilungen.length" class="m-team__abteilungen">
        <div :key="index" v-for="(abteilung, index) in filteredAbteilungen">
          <section v-if="filteredMitgliederForAbteilung(abteilung).length" class="m-team__abteilung revealabteilung mb-20">
            <h2 class="mb-4">{{abteilung.bezeichnung}}</h2>
            <section class="m-team__members grid md:grid-cols-2 gap-10 xl:grid-cols-3 2xl:grid-cols-4 mt-8">
              <article :key="mitglied.urlsegment" v-for="mitglied in filteredMitgliederForAbteilung(abteilung)">
                <Member v-bind="mitglied"></Member>
              </article>
            </section>
          </section>
        </div>
      </div>

      <div v-else-if="search.length > 0" class="py-8">
        <p>No team members found matching "{{ search }}"</p>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import Member from './Member';
import { gsap } from 'gsap';
import { CSSPlugin } from 'gsap/CSSPlugin'
import { ScrollTrigger } from "gsap/ScrollTrigger.js";
gsap.registerPlugin(CSSPlugin);
gsap.registerPlugin(ScrollTrigger);

// Cache duration in milliseconds (1 hour)
const CACHE_DURATION = 60 * 60 * 1000;

export default {
    name: 'TeamComponent',
    components: {
        Member
    },
    data: function() {
        return {
            abteilungen: null,
            search: "",
            placeholder: "Name/Abteilung suchen",
            width: 0,
            loading: true,
            debounceTimeout: null
        };
    },
    computed: {
        filteredAbteilungen() {
            if (!this.abteilungen) return [];
            if (!this.search.length) return this.abteilungen;

            return this.abteilungen.filter(abteilung =>
                this.searchStringInBezeichnung(this.search, abteilung.bezeichnung) ||
                this.searchStringInMitglieder(this.search, abteilung.mitglieder)
            );
        }
    },
    created: function() {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    },
    unmounted() {
        window.removeEventListener('resize', this.handleResize);
    },
    mounted: function () {
        // Init
        this.getAbteilungen();
    },
    watch: {
        width(newValue) {
            if(newValue <= 1600) {
                this.placeholder = "Name/Abteilung suchen"
            }
            else {
                this.placeholder = "Name oder Abteilung suchen"
            }
        }
    },
    methods: {
        handleResize() {
            this.width = window.innerWidth;
        },
        onSearchInput(e) {
            // Debounce search input
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = setTimeout(() => {
                this.search = e.target.value;
            }, 300);
        },
        filteredMitgliederForAbteilung(abteilung) {
            if (!this.search.length) return abteilung.mitglieder;

            if (this.searchStringInBezeichnung(this.search, abteilung.bezeichnung)) {
                return abteilung.mitglieder;
            } else {
                return this.filteredMitarbeiter(this.search, abteilung.mitglieder);
            }
        },
        animation: function() {
            // Optimize animations by only targeting visible elements
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const elem = entry.target;
                        gsap.from(elem, {
                            y: 25,
                            opacity: 0,
                            duration: 0.5,
                        });

                        // Stop observing after animation
                        observer.unobserve(elem);
                    }
                });
            });

            // Observe all abteilung elements
            document.querySelectorAll('.revealabteilung').forEach(elem => {
                observer.observe(elem);
            });
        },
        getBaseUrl: function() {
            // npm run serve
            if(process.env.NODE_ENV === "development") {
                return 'http://weberprevost.test'
            }
            return ''
        },
        /* low level search in mitglied */
        searchInMitglied: function(searchStr, mitglied) {
            let search = searchStr.trim().toLowerCase();
            let vorname = (mitglied.vorname?.length) ? mitglied.vorname.trim().toLowerCase() : "";
            let nachname = (mitglied.nachname?.length) ? mitglied.nachname.trim().toLowerCase() : "";
            let funktion = (mitglied.funktion?.length) ? mitglied.funktion.trim().toLowerCase() : "";
            let telefon = (mitglied.telefon?.length) ? mitglied.telefon.trim().toLowerCase() : "";
            let email = (mitglied.email?.length) ? mitglied.email.trim().toLowerCase() : "";
            let standorte = (mitglied.standorte?.length) ? mitglied.standorte[0].name.trim().toLowerCase() : "";

            // Search in mitglied
            let data = vorname + nachname + funktion + telefon + email + standorte;
            return (data.indexOf(search) !== -1);
        },
        filteredMitarbeiter: function(searchStr, mitglieder) {
            let search = searchStr.trim().toLowerCase();
            return mitglieder.filter(mitglied => this.searchInMitglied(search, mitglied));
        },
        searchStringInBezeichnung: function(searchStr, bezeichnung) {
            let search = searchStr.trim().toLowerCase();
            let bez = bezeichnung.trim().toLowerCase();
            return bez.indexOf(search) >= 0;
        },
        searchStringInMitglieder: function(searchStr, mitglieder) {
            let search = searchStr.trim().toLowerCase();
            return mitglieder.some(mitglied => this.searchInMitglied(search, mitglied));
        },
        getAbteilungen: function() {
            // Try to get data from cache first
            const cachedData = this.getFromCache('teamData');
            if (cachedData) {
                this.abteilungen = cachedData;
                this.loading = false;
                this.$nextTick(() => {
                    this.animation();
                });
                return;
            }

            // Fetch from API if not in cache
            let baseUrl = this.getBaseUrl();
            let params = {
                params: {
                    "order[sorting]": "asc"
                }
            };

            axios.get(baseUrl + '/_api/team/abteilung', params)
                .then(response => {
                    let apiResponseData = response.data;
                    this.abteilungen = apiResponseData['hydra:member'];

                    // Save to cache
                    this.saveToCache('teamData', this.abteilungen);

                    this.loading = false;
                    this.$nextTick(() => {
                        this.animation();
                    });
                })
                .catch(error => {
                    console.error('Error fetching team data:', error);
                    this.loading = false;
                });
        },
        saveToCache(key, data) {
            try {
                const cacheItem = {
                    data: data,
                    timestamp: Date.now()
                };
                localStorage.setItem(key, JSON.stringify(cacheItem));
            } catch (e) {
                console.error('Error saving to cache:', e);
            }
        },
        getFromCache(key) {
            try {
                const cacheItem = JSON.parse(localStorage.getItem(key));
                if (!cacheItem) return null;

                // Check if cache is expired
                const now = Date.now();
                if (now - cacheItem.timestamp > CACHE_DURATION) {
                    localStorage.removeItem(key);
                    return null;
                }

                return cacheItem.data;
            } catch (e) {
                console.error('Error reading from cache:', e);
                return null;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.m-team {
    /* Remove min-height to prevent layout shifts */
    min-height: auto;
    opacity: 1;
}
</style>
